import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import axios from "axios";
import { toast } from "react-toastify";
import {
  esErrorServicioNoActivo,
  limpiarServicioActivoLocal,
} from "../../utils/servicioActivo";
import { useNavigate } from "react-router-dom";

import SelectField from "../ui/SelectField";
import TextAreaField from "../ui/TextAreaField";
import {
  ClipboardCheck,
  Package,
  Trash2,
  PlusCircle,
  Save,
  Lock,
  AlertTriangle,
  CheckSquare2,
  Loader2,
} from "lucide-react";

const FormPreventivo = ({ servicio, gestionData, canEdit = false }) => {
  const [rutinas, setRutinas] = useState([]);
  const [checkedRutinas, setCheckedRutinas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [repuestos, setRepuestos] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [selectedRepuesto, setSelectedRepuesto] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [gestionExistente, setGestionExistente] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id;
  const canEditRepuestos = servicio.estado === "Pendiente";

  // 🚨 Validar acceso
  useEffect(() => {
    if (!servicio) return;
    const estado = servicio.estado;
    const esValido = ["Pendiente", "Completado"].includes(estado);

    if (!esValido) {
      localStorage.removeItem("servicioActivo");
      toast.error(
        "⚠️ Gestión no permitida: el servicio no está en un estado válido",
      );
      setTimeout(() => navigate("/my_services"), 1500);
    }
  }, [servicio, navigate]);

  // 🚀 Rutinas
  useEffect(() => {
    const fetchRutinas = async () => {
      if (!servicio.equipo?.categoria?._id) {
        toast.error("El equipo no tiene una categoría asignada");
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/cat/${servicio.equipo.categoria._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setRutinas(res.data.rutinas || []);
      } catch {
        toast.error("Error al cargar rutinas de mantenimiento");
      } finally {
        setLoading(false);
      }
    };
    fetchRutinas();
  }, [API, token, servicio.equipo?.categoria?._id]);

  // 🚀 Catálogo
  useEffect(() => {
    const fetchCatalogo = async () => {
      try {
        const res = await axios.get(`${API}/stock/stock-alma-servicios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCatalogo(res.data);
      } catch {
        toast.error("No se pudieron cargar los repuestos del almacén");
      }
    };
    fetchCatalogo();
  }, [API, token]);

  // 🧩 Gestión existente
  useEffect(() => {
    const fetchGestion = async () => {
      if (servicio.estado !== "Completado") return;
      if (gestionData) {
        setGestionExistente(gestionData);
        setCheckedRutinas(gestionData.rutinasCompletadas || []);
        setRepuestos(gestionData.repuestosUsados || []);
        setObservaciones(gestionData.observaciones || "");
        return;
      }
      try {
        const res = await axios.get(
          `${API}/gestion/cronograma/${servicio._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data?.length) {
          const g = res.data[0];
          setGestionExistente(g);
          setCheckedRutinas(g.rutinasCompletadas || []);
          setRepuestos(g.repuestosUsados || []);
          setObservaciones(g.observaciones || "");
        }
      } catch {
        toast.error("No se pudo cargar la gestión registrada");
      }
    };
    fetchGestion();
  }, [API, token, servicio, gestionData]);

  const handleCheck = (rutina) => {
    setCheckedRutinas((prev) =>
      prev.includes(rutina)
        ? prev.filter((r) => r !== rutina)
        : [...prev, rutina],
    );
  };

  const agregarRepuesto = () => {
    const rep = catalogo.find((r) => r._id === selectedRepuesto);
    if (!rep) return toast.warning("Seleccione un repuesto válido");
    if (repuestos.some((r) => (r.repuestoId || r._id) === rep._id))
      return toast.info("Este repuesto ya fue agregado");
    if (!rep.almacenId)
      return toast.error("Este repuesto no tiene almacén asociado");

    setRepuestos((prev) => [
      ...prev,
      { ...rep, almacenId: rep.almacenId, cantidad_usada: 1 },
    ]);

    setSelectedRepuesto("");
    toast.success("Repuesto agregado");
  };

  const eliminarRepuesto = (id) => {
    setRepuestos((prev) => prev.filter((r) => (r._id || r.repuestoId) !== id));
  };

  const handleGuardar = async () => {
    if (!checkedRutinas.length)
      return toast.warning("Debe marcar al menos una rutina realizada");

    setIsLoading(true);
    try {
      const payloadBase = {
        cronograma_id: servicio._id,
        tipo: "Preventivo",
        rutinasCompletadas: checkedRutinas,
        observaciones,
        usuarioId: userId,
      };

      if (servicio.estado === "Completado" && gestionExistente?._id) {
        await axios.put(`${API}/gestion/${gestionExistente._id}`, payloadBase, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("✅ Mantenimiento preventivo actualizado");
        setTimeout(() => navigate("/my_services"), 1500);
      } else {
        const confirmado = window.confirm(
          "Al guardar esta gestión, los repuestos utilizados no se podrán modificar posteriormente. ¿Deseas continuar?",
        );
        if (!confirmado) {
          setIsLoading(false);
          return;
        }

        const payload = {
          ...payloadBase,
          repuestosUsados: repuestos.map((r) => ({
            repuestoId: r.repuestoId || r._id,
            articulo: r.articulo,
            codigo_item: r.codigo_item,
            cantidad_usada: r.cantidad_usada || 1,
            almacenId: r.almacenId,
          })),
        };

        await axios.post(`${API}/gestion`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        localStorage.removeItem("servicioActivo");
        toast.success("✅ Mantenimiento preventivo guardado correctamente");
        setTimeout(() => navigate("/my_services"), 1500);
      }
    } catch (err) {
      if (esErrorServicioNoActivo(err)) limpiarServicioActivoLocal();
      toast.error(
        err.response?.data?.message || "Error al guardar el mantenimiento",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeleccionarTodas = () => {
    setCheckedRutinas([...rutinas]);
  };

  const handleDeseleccionarTodas = () => {
    setCheckedRutinas([]);
  };

  const progreso = rutinas.length
    ? Math.round((checkedRutinas.length / rutinas.length) * 100)
    : 0;

  return (
    <div className="space-y-6">

      {/* Título sección */}
      <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100">
        <div className="bg-blue-100 rounded-lg p-1.5">
          <ClipboardCheck className="w-4 h-4 text-blue-600" />
        </div>
        <h3 className="text-base font-semibold text-gray-800">
          Mantenimiento preventivo
        </h3>
      </div>

      {/* Banners de info */}
      {servicio.estado === "Completado" && gestionExistente && (
        <p className="text-sm italic text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5">
          Mostrando la gestión previamente registrada.
        </p>
      )}

      {servicio.estado === "Completado" && canEdit && (
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Los repuestos quedan bloqueados después del primer guardado y no se
          pueden modificar en edición.
        </div>
      )}

      {/* ── Rutinas de mantenimiento ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <CheckSquare2 className="w-4 h-4 text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-700">
            Rutinas de mantenimiento
          </h4>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando rutinas…
          </div>
        ) : rutinas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center border-2 border-dashed border-gray-200 rounded-xl">
            <CheckSquare2 className="w-7 h-7 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">No hay rutinas configuradas.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Barra de progreso */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs text-gray-500 font-medium">Progreso</span>
                <span className="text-xs font-semibold text-blue-600">
                  {checkedRutinas.length} / {rutinas.length} completadas
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progreso}%` }}
                />
              </div>
            </div>

            {/* Botones seleccionar/deseleccionar */}
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSeleccionarTodas}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Seleccionar todas
                </button>
                <button
                  type="button"
                  onClick={handleDeseleccionarTodas}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Deseleccionar todas
                </button>
              </div>
            )}

            {/* Lista de rutinas */}
            <div className="space-y-2">
              {rutinas.map((rutina, idx) => {
                const checked = checkedRutinas.includes(rutina);
                return (
                  <label
                    key={idx}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                      checked
                        ? "bg-blue-50 border-blue-200"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                  >
                    {/* Checkbox visual personalizado */}
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        checked
                          ? "bg-blue-500 border-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {checked && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleCheck(rutina)}
                      disabled={!canEdit}
                      className="sr-only"
                    />
                    <span
                      className={`text-sm ${
                        checked
                          ? "text-blue-700 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      {rutina}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Repuestos ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">
              Repuestos utilizados
            </h3>
          </div>
          {repuestos.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
              {repuestos.length}
            </span>
          )}
        </div>

        {canEditRepuestos && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end bg-gray-50 rounded-xl p-3">
            <div className="w-full sm:flex-1">
              <SelectField
                label="Seleccionar repuesto"
                name="repuesto"
                value={selectedRepuesto}
                options={catalogo}
                onChange={(e) => setSelectedRepuesto(e.target.value)}
                displayFn={(r) =>
                  `${r.articulo} (${
                    r.precio_unitario
                      ? `$ ${Number(r.precio_unitario).toLocaleString()}`
                      : "—"
                  }) (${r.stock_actual} disponibles)`
                }
              />
            </div>
            <Button
              className="flex items-center gap-1.5 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              onClick={agregarRepuesto}
            >
              <PlusCircle className="w-4 h-4" />
              Agregar
            </Button>
          </div>
        )}

        {repuestos.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Artículo</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Código</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cant.</th>
                  {canEditRepuestos && (
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Acción
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {repuestos.map((r, idx) => (
                  <tr
                    key={r._id || r.repuestoId}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-400 text-xs font-medium">{idx + 1}</td>
                    <td className="px-4 py-3 text-gray-800 font-medium">{r.articulo}</td>
                    <td className="px-4 py-3 text-gray-500">{r.codigo_item || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-gray-100 rounded-lg text-gray-700 font-semibold text-xs">
                        {r.cantidad_usada}
                      </span>
                    </td>
                    {canEditRepuestos && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => eliminarRepuesto(r._id || r.repuestoId)}
                          className="flex items-center gap-1 ml-auto text-red-400 hover:text-red-600 text-xs font-medium transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Quitar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          canEditRepuestos && (
            <div className="flex flex-col items-center justify-center py-6 text-center border-2 border-dashed border-gray-200 rounded-xl">
              <Package className="w-7 h-7 text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Sin repuestos agregados</p>
            </div>
          )
        )}
      </div>

      {/* ── Observaciones ── */}
      <div className="space-y-1.5">
        <TextAreaField
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          readOnly={!canEdit}
          rows={4}
        />
        {!canEdit && (
          <p className="flex items-center gap-1.5 text-xs text-gray-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            Solo lectura
          </p>
        )}
      </div>

      {/* ── Guardar ── */}
      {canEdit && (
        <div className="pt-2 border-t border-gray-100">
          <Button
            onClick={handleGuardar}
            disabled={isLoading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-6"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isLoading ? "Guardando..." : "Guardar preventivo"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FormPreventivo;
